import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BaseButton,
  FilterBottomSheet,
  formatDateToShortDotV2,
  Toast,
  useIsMobile,
} from '@repo/common';

import EmployerApplicationModal from './EmployerApplicationModal';
import JobBoardPagination from '../../../JobBoard/JobBoardPagination';
import HeaderText from '../../Common/HeaderText';
import HeaderTab from '../Applications/HeaderTab';
import FilterList from '../Applications/FilterList';
import {
  getEmployerApplicationCounts,
  getEmployerApplications,
  updateEmployerApplicationStatus,
} from '../../../../api/jobboard/apply.api';
import { getJobPosts } from '../../../../api/jobboard/jobboard.api';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';

const PAGE_SIZE = 7;
const TAB_CONFIG = [
  { key: 'needsReview', label: '확인필요', status: 'SUBMITTED' },
  { key: 'accepted', label: '수락', status: 'ACCEPTED' },
  { key: 'rejected', label: '탈락', status: 'NEEDPREP' },
  { key: 'pastJobs', label: '지난공고' },
];
const PERIOD_OPTIONS = [
  { value: 'ALL', label: 'All time' },
  { value: '1M', label: 'Last 1 month' },
  { value: '3M', label: 'Last 3 month' },
  { value: '4M', label: 'Last 4 month' },
];

const EmployerFilter = ({
  label,
  options,
  selectedValue,
  onSelect,
  isMobile,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  if (isMobile) {
    return (
      <>
        <BaseButton
          type='text_basic'
          size='s'
          icon={isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
          bgclass='bg-transparent border-none pl-1 pr-2 py-1'
          onClick={() => setIsOpen(true)}
          textWrapperClass='max-w-[130px] truncate'
        >
          {label}
        </BaseButton>
        {isOpen && (
          <FilterBottomSheet
            options={options.map((option) => option.label)}
            selected={label}
            onSelect={(selectedLabel) => {
              const selectedOption = options.find(
                (option) => option.label === selectedLabel,
              );
              onSelect(selectedOption?.value ?? null);
              setIsOpen(false);
            }}
            onClose={() => setIsOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <div ref={filterRef} className='relative min-w-0'>
      <BaseButton
        type='text_basic'
        size='s'
        icon={isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        bgclass='pl-2'
        gap='gap-1'
        textWrapperClass='max-w-[180px] truncate'
      >
        {label}
      </BaseButton>

      {isOpen && (
        <div className='absolute left-0 top-[34px] z-30'>
          <FilterList
            selectedOption={selectedValue}
            options={options.map((option) => ({
              optionLabel: option.label,
              optionValue: option.value,
            }))}
            onSelect={(value) => {
              onSelect(value);
              setIsOpen(false);
            }}
            onClose={() => setIsOpen(false)}
            manageOutsideClick={false}
            className='h-auto max-h-[260px] w-[220px]'
          />
        </div>
      )}
    </div>
  );
};

const isPastJob = (job) => {
  if (job.status === 'CLOSED' || job.status?.startsWith('DELETED_')) {
    return true;
  }

  return (
    job.applicationDeadline &&
    new Date(job.applicationDeadline).getTime() < Date.now()
  );
};

const EmployerJobs = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedTab, setSelectedTab] = useState('needsReview');
  const [items, setItems] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [listError, setListError] = useState('');
  const requestVersion = useRef(0);
  const [counts, setCounts] = useState({
    needsReview: 0,
    accepted: 0,
    rejected: 0,
    pastJobs: 0,
  });
  const [pastJobs, setPastJobs] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [periodFilter, setPeriodFilter] = useState('ALL');
  const [jobFilter, setJobFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [toastTitle, setToastTitle] = useState('');
  const [acceptingApplication, setAcceptingApplication] = useState(null);
  const [officialStartDate, setOfficialStartDate] = useState('');

  const fetchCountsAndPastJobs = useCallback(async () => {
    try {
      const [applicationCounts, ownJobPosts] = await Promise.all([
        getEmployerApplicationCounts(),
        getJobPosts(),
      ]);
      const nextPastJobs = (ownJobPosts || []).filter(isPastJob);
      const nextActiveJobs = ownJobPosts || [];

      setPastJobs(nextPastJobs);
      setActiveJobs(nextActiveJobs);
      setCounts({
        needsReview: applicationCounts?.needsReview ?? 0,
        accepted: applicationCounts?.accepted ?? 0,
        rejected: applicationCounts?.rejected ?? 0,
        pastJobs: nextPastJobs.length,
      });
    } catch (error) {
      console.error('Failed to fetch employer job counts:', error);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    const tab = TAB_CONFIG.find(({ key }) => key === selectedTab);
    if (!tab?.status) return;
    const version = ++requestVersion.current;

    try {
      setIsLoading(true);
      setListError('');
      const data = await getEmployerApplications(
        tab.status,
        currentPage - 1,
        PAGE_SIZE,
        periodFilter,
        jobFilter,
        keyword,
      );
      if (version !== requestVersion.current) return;
      setItems(Array.isArray(data?.content) ? data.content : []);
      setTotalPages(Math.max(data?.totalPages || 1, 1));
    } catch (error) {
      if (version !== requestVersion.current) return;
      setListError('지원 목록을 불러오지 못했습니다. 다시 시도해 주세요.');
      console.error('Failed to fetch employer applications:', error);
      setItems([]);
      setTotalPages(1);
    } finally {
      if (version === requestVersion.current) setIsLoading(false);
    }
  }, [currentPage, jobFilter, periodFilter, selectedTab, keyword]);

  useEffect(() => {
    fetchCountsAndPastJobs();
  }, [fetchCountsAndPastJobs]);

  useEffect(() => {
    if (selectedTab === 'pastJobs') {
      requestVersion.current += 1;
      setListError('');
      const periodMonths = { '1M': 1, '3M': 3, '4M': 4 }[periodFilter];
      const periodStart = periodMonths
        ? new Date(new Date().setMonth(new Date().getMonth() - periodMonths))
        : null;
      const filteredPastJobs = periodStart
        ? pastJobs.filter(
            (job) =>
              new Date(job.publishedAt || job.createdAt).getTime() >=
              periodStart.getTime(),
          )
        : pastJobs;
      const pastTotalPages = Math.max(
        Math.ceil(filteredPastJobs.length / PAGE_SIZE),
        1,
      );
      setTotalPages(pastTotalPages);
      setItems(
        filteredPastJobs.slice(
          (currentPage - 1) * PAGE_SIZE,
          currentPage * PAGE_SIZE,
        ),
      );
      setIsLoading(false);
      return;
    }

    fetchApplications();
  }, [currentPage, fetchApplications, pastJobs, periodFilter, selectedTab]);

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setCurrentPage(1);
    if (tab === 'pastJobs') setJobFilter(null);
  };

  const handleStatusChange = async (
    applicationId,
    status,
    startDate = null,
  ) => {
    try {
      setUpdatingId(applicationId);
      await updateEmployerApplicationStatus(applicationId, status, startDate);
      setToastTitle(status === 'ACCEPTED' ? '지원자를 수락했습니다.' : '지원자를 탈락 처리했습니다.');
      setAcceptingApplication(null);
      setOfficialStartDate('');
      await Promise.all([fetchApplications(), fetchCountsAndPastJobs()]);
    } catch (error) {
      console.error('Failed to update application status:', error);
    } finally {
      setUpdatingId(null);
    }
  };

  const emptyMessage = useMemo(() => {
    const messages = {
      needsReview: '확인이 필요한 지원자가 없습니다.',
      accepted: '수락한 지원자가 없습니다.',
      rejected: '탈락 처리한 지원자가 없습니다.',
      pastJobs: '지난 공고가 없습니다.',
    };
    return messages[selectedTab];
  }, [selectedTab]);

  const selectedPeriodLabel =
    PERIOD_OPTIONS.find(({ value }) => value === periodFilter)?.label ||
    'All time';
  const selectedJobLabel =
    activeJobs.find(({ id }) => id === jobFilter)?.title || 'All Jobs';
  const jobOptions = [
    { value: null, label: 'All Jobs' },
    ...activeJobs.map((job) => ({ value: job.id, label: [job.companyName, job.title].filter(Boolean).join(' · ') })),
  ];
  const todayInSeoul = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
  }).format(new Date());

  const openAcceptModal = (application) => {
    setAcceptingApplication(application);
    setOfficialStartDate(todayInSeoul);
  };

  return (
    <div className='relative flex flex-col w-full items-start tabletH:pb-[60px]'>
      <HeaderText label={isAdmin ? '전체 지원 현황' : 'Jobs'} />
      <p className='mb-4 typo-b14 text-text-secondary'>
        {isAdmin ? '사용자가 어느 회사와 공고에 지원했는지 확인하고 제출 서류를 열람할 수 있습니다.' : '지원서 보기를 눌러 지원자의 제출 이력서와 첨부 자료를 확인하세요.'}
      </p>
      <HeaderTab
        counts={counts}
        selectedTab={selectedTab}
        onChangeTab={handleTabChange}
        $isMobile={isMobile}
        tabs={TAB_CONFIG}
        mobileTabs={TAB_CONFIG}
      />

      <div className='flex flex-col w-full items-start p-0 tabletH:px-4 tabletH:py-6 bg-transparent tabletH:bg-grayscale-gray00 rounded-[15px]'>
        {selectedTab !== 'pastJobs' && (
          <form className='mb-4 flex w-full gap-2' onSubmit={(event) => {
            event.preventDefault(); setKeyword(searchInput.trim()); setCurrentPage(1);
          }}>
            <input aria-label='지원자, 이메일, 회사명, 공고명 검색' value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder='지원자 · 이메일 · 회사명 · 공고명 검색' className='min-w-0 flex-1 rounded-lg border border-grayscale-gray02 px-3 py-2 typo-b14' />
            <button type='submit' className='rounded-lg bg-grayscale-gray02 px-4 typo-b14Strong'>검색</button>
            {keyword && <button type='button' className='px-2 typo-b13' onClick={() => { setSearchInput(''); setKeyword(''); setCurrentPage(1); }}>초기화</button>}
          </form>
        )}
        <div className='flex w-full items-center gap-2 pb-4'>
          <EmployerFilter
            label={selectedPeriodLabel}
            options={PERIOD_OPTIONS}
            selectedValue={periodFilter}
            isMobile={isMobile}
            onSelect={(value) => {
              setPeriodFilter(value);
              setCurrentPage(1);
            }}
          />
          {selectedTab !== 'pastJobs' && (
            <EmployerFilter
              label={selectedJobLabel}
              options={jobOptions}
              selectedValue={jobFilter}
              isMobile={isMobile}
              onSelect={(value) => {
                setJobFilter(value);
                setCurrentPage(1);
              }}
            />
          )}
        </div>

        {!isMobile && (
          <div className='flex w-full items-center pb-4 bg-grayscale-gray00 typo-b13Strong text-text-primary'>
            {selectedTab === 'pastJobs' ? (
              <>
                <div className='flex min-w-0 flex-1 items-center px-2 py-4'>
                  Job title
                </div>
                <div className='flex w-full max-w-[140px] items-center justify-center px-2 py-4'>
                  Status
                </div>
                <div className='flex w-full max-w-[130px] items-center justify-center px-2 py-4'>
                  Posted on
                </div>
              </>
            ) : (
              <>
                <div className='flex w-full max-w-[140px] items-center px-2 py-4'>
                  Applicant
                </div>
                <div className='flex min-w-0 flex-1 items-center justify-center px-2 py-4'>
                  Job title
                </div>
                <div className='flex w-full max-w-[110px] items-center justify-center px-2 py-4'>
                  Applied by
                </div>
                <div className='flex w-full max-w-[260px] items-center justify-center px-2 py-4'>
                  Action
                </div>
              </>
            )}
          </div>
        )}

        {isLoading ? (
          <div className='flex h-[300px] w-full items-center justify-center p-10 text-center typo-b14 text-text-placeholder tabletH:h-[380px]'>
            불러오는 중...
          </div>
        ) : listError ? (
          <div className='flex flex-col gap-3 p-6'><p role='alert'>{listError}</p><BaseButton size='s' hideIcon onClick={fetchApplications}>다시 시도</BaseButton></div>
        ) : items.length === 0 ? (
          <div className='flex h-[300px] w-full items-center justify-center p-10 text-center typo-b14 text-text-placeholder tabletH:h-[380px]'>
            {emptyMessage}
          </div>
        ) : selectedTab === 'pastJobs' ? (
          <div className='flex flex-col gap-2 w-full pb-10'>
            {items.map((job) => (
              <button
                key={job.id}
                type='button'
                onClick={() => navigate(`/jobs/${job.id}`)}
                className='flex w-full items-center gap-2 py-2 text-left typo-b14 text-text-secondary'
              >
                <span className='min-w-0 flex-1 truncate px-2 typo-b14Strong text-text-primary tabletH:typo-b14'>
                  {job.title}
                </span>
                <span className='hidden w-full max-w-[140px] px-2 text-center typo-b13 text-text-secondary tabletH:block'>
                  {job.status === 'CLOSED' ? '마감' : '종료'}
                </span>
                <span className='w-full max-w-[130px] px-2 text-right typo-b13 text-text-secondary tabletH:text-center'>
                  {formatDateToShortDotV2(job.publishedAt || job.createdAt)}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className='flex flex-col gap-2 w-full pb-10'>
            {items.map((application) => (
              <div
                key={application.applicationId}
                className='flex flex-col tabletH:grid tabletH:grid-cols-[140px_minmax(0,1fr)_110px_260px] tabletH:items-center gap-2 w-full py-2 typo-b14 text-text-secondary'
              >
                <div className='flex min-w-0 flex-col px-2'>
                  <span className='truncate typo-b14Strong text-text-primary'>
                    {application.applicantName}
                  </span>
                  <span className='truncate typo-b12 text-text-secondary'>
                    {application.email}
                  </span>
                  {isAdmin && <span className='typo-b12 text-text-placeholder'>회원 #{application.applicantUserId}</span>}
                </div>
                <button
                  type='button'
                  onClick={() => navigate(`/jobs/${application.jobPostId}`)}
                  className='min-w-0 truncate px-2 text-left underline underline-offset-2 typo-b14 text-text-primary tabletH:text-center'
                >
                  <span className='block typo-b12 text-text-secondary no-underline'>{application.companyName}</span>
                  <span>{application.jobTitle}</span>
                </button>
                <span className='px-2 typo-b12 text-text-secondary tabletH:text-center'>
                  {formatDateToShortDotV2(application.createdAt)}
                </span>
                <div className='flex flex-wrap items-center justify-end gap-2'>
                  {isAdmin && <span className='typo-b12'>{TAB_CONFIG.find((tab) => tab.status === application.status)?.label}</span>}
                  <BaseButton type='secondary' size='s' hideIcon rounded='none' onClick={() => setSelectedApplicationId(application.applicationId)}>지원서 보기</BaseButton>
                  {!isAdmin && selectedTab !== 'rejected' && (
                    <BaseButton
                      type='secondary'
                      size='s'
                      hideIcon
                      rounded='none'
                      isDisabled={updatingId === application.applicationId}
                      onClick={() =>
                        handleStatusChange(
                          application.applicationId,
                          'NEEDPREP',
                        )
                      }
                    >
                      탈락
                    </BaseButton>
                  )}
                  {!isAdmin && selectedTab !== 'accepted' && (
                    <BaseButton
                      size='s'
                      hideIcon
                      rounded='none'
                      isDisabled={updatingId === application.applicationId}
                      onClick={() => openAcceptModal(application)}
                    >
                      수락
                    </BaseButton>
                  )}
                </div>
                {selectedTab === 'accepted' &&
                  application.retentionExpiresAt && (
                    <p className='tabletH:col-span-4 typo-b12 text-text-placeholder'>
                      지원자 정보 열람 기한:{' '}
                      {formatDateToShortDotV2(
                        application.retentionExpiresAt,
                      )}
                    </p>
                  )}
              </div>
            ))}
          </div>
        )}

        <JobBoardPagination
          current={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          isMyPage
        />
      </div>

      {selectedApplicationId && (
        <EmployerApplicationModal applicationId={selectedApplicationId} isMobile={isMobile} onClose={() => setSelectedApplicationId(null)} />
      )}

      {toastTitle && (
        <div className='fixed bottom-6 left-1/2 z-[1000] -translate-x-1/2'>
          <Toast
            type='success'
            state='white'
            title={toastTitle}
            hierarchy='important'
            direction='up'
          />
        </div>
      )}

      {acceptingApplication && (
        <div className='fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 px-4'>
          <div className='flex w-full max-w-[420px] flex-col gap-6 bg-grayscale-gray00 p-6 shadow-popup-02'>
            <div className='flex flex-col gap-2'>
              <h2 className='typo-title2 text-text-primary'>공식 입사일 입력</h2>
              <p className='typo-b14 text-text-secondary'>
                합격 처리 후 지원자 정보는 공식 입사일로부터 최대 90일까지
                열람할 수 있으며, 이후 자동 삭제됩니다.
              </p>
            </div>

            <label className='flex flex-col gap-2'>
              <span className='typo-b14Strong text-text-primary'>
                공식 입사일<span className='text-text-accent'>*</span>
              </span>
              <input
                type='date'
                min={todayInSeoul}
                value={officialStartDate}
                onChange={(event) => setOfficialStartDate(event.target.value)}
                className='h-12 w-full border border-grayscale-gray02 bg-grayscale-gray00 px-4 typo-b14 text-text-primary outline-none focus:border-green-deep02'
              />
            </label>

            <div className='flex justify-end gap-2'>
              <BaseButton
                type='secondary'
                size='l'
                hideIcon
                rounded='none'
                onClick={() => {
                  setAcceptingApplication(null);
                  setOfficialStartDate('');
                }}
              >
                취소
              </BaseButton>
              <BaseButton
                size='l'
                hideIcon
                rounded='none'
                isDisabled={
                  !officialStartDate ||
                  officialStartDate < todayInSeoul ||
                  updatingId === acceptingApplication.applicationId
                }
                onClick={() =>
                  handleStatusChange(
                    acceptingApplication.applicationId,
                    'ACCEPTED',
                    officialStartDate,
                  )
                }
              >
                수락 확정
              </BaseButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerJobs;
